import React, {useState, useEffect} from "react"
import {
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Grid,
	Chip,
	Stack,
} from "@mui/material"
import {ExpandMore} from "@mui/icons-material"
import styled from "styled-components"

import {ExtendInstalmentCapButton} from "./ExtendInstalmentCapButton"
import {ResetInstalmentCapButton} from "./ResetInstalmentCapButton"

import {colors} from "@indebted/theme"
import {api} from "@indebted/api"
import type {InstalmentCapHistoryEntry, InstalmentCapsResponse} from "@indebted/api"
import {LoadingPage} from "@indebted/components/Loading"

type InstalmentCapData = {
	currentMaxPayments: number
	currentMinPayment: string
	maximumCapAvailable: number
}

type DefaultInstalmentCapData = {
	defaultMaxPayments: number
	defaultMinPayment: string
}

type InstalmentCapProps = {
	data: InstalmentCapData
	defaultData: DefaultInstalmentCapData
	history: InstalmentCapHistoryEntry[]
	customerID: string
	maximumExtendableDuration: number
	defaultMaxPaymentsWeekly: number
	currentMaxPaymentsWeekly: number
	totalCustomerBalance: number
	onExtendSuccess: () => void
	onResetSuccess: () => void
	onResetError: (error: string) => void
	showResetButton?: boolean
}

const mapTypeToDisplay = (type: string): string => {
	if (type === "Override") {
		return "Extension"
	}
	return type
}

const InstalmentCap = ({
	data,
	defaultData,
	history,
	customerID,
	maximumExtendableDuration,
	defaultMaxPaymentsWeekly,
	currentMaxPaymentsWeekly,
	totalCustomerBalance,
	onExtendSuccess,
	onResetSuccess,
	onResetError,
	showResetButton,
}: InstalmentCapProps) => {
	const hasHistory = history.length > 0
	const [expanded, setExpanded] = useState(hasHistory)
	const isExtended = currentMaxPaymentsWeekly !== defaultMaxPaymentsWeekly
	const isAtMaximum = currentMaxPaymentsWeekly === maximumExtendableDuration

	const handleAccordionChange = () => {
		setExpanded(!expanded)
	}

	return (
		<StyledContainer>
			<Stack direction="column" display="flex" gap="1rem">
				{/* Header Section */}
				<Box display="flex" justifyContent="space-between" alignItems="flex-start">
					<Box>
						<Stack display="flex" flexDirection="row" gap="0.75rem">
							<Typography variant="h5" gutterBottom>
								Instalment cap
							</Typography>

							{/* Override Active Badge */}
							{isExtended && (
								<Box>
									<OverrideActiveChip label="Extension active" size="small" />
								</Box>
							)}
						</Stack>
					</Box>
					<Stack direction="row" gap="1rem">
						{showResetButton && (
							<ResetInstalmentCapButton
								customerID={customerID}
								onResetSuccess={onResetSuccess}
								onResetError={onResetError}
							/>
						)}
						<ExtendInstalmentCapButton
							customerID={customerID}
							maximumExtendableDuration={maximumExtendableDuration}
							defaultMaxPaymentsWeekly={defaultMaxPaymentsWeekly}
							currentMaxPaymentsWeekly={currentMaxPaymentsWeekly}
							totalCustomerBalance={totalCustomerBalance}
							onSuccess={onExtendSuccess}
						/>
					</Stack>
				</Box>

				{/* Active Summary Table */}
				{isExtended && (
					<Box>
						<TableTitle variant="body1" color="textSecondary">
							Active extension in place
						</TableTitle>
						<SummaryTableContainer $isExtended={isExtended} $isAtMaximum={isAtMaximum}>
							<Table size="small">
								<StyledTableHead $isExtended={isExtended} $isAtMaximum={isAtMaximum}>
									<TableRow>
										<StyledHeaderCell
											$isExtended={isExtended}
											$isAtMaximum={isAtMaximum}
											width="33%"
										>
											Balance
										</StyledHeaderCell>
										<StyledHeaderCell
											$isExtended={isExtended}
											$isAtMaximum={isAtMaximum}
											width="33%"
										>
											Max # of instalments
										</StyledHeaderCell>
										<StyledHeaderCell
											$isExtended={isExtended}
											$isAtMaximum={isAtMaximum}
											width="34%"
										>
											Min payment
										</StyledHeaderCell>
									</TableRow>
								</StyledTableHead>
								<TableBody>
									<TableRow>
										<StyledTableCell $isExtended={isExtended}>
											<Typography variant="body2">${totalCustomerBalance.toFixed(2)}</Typography>
										</StyledTableCell>
										<StyledTableCell $isExtended={isExtended}>
											<Typography variant="body2">{data.currentMaxPayments}</Typography>
										</StyledTableCell>
										<StyledTableCell $isExtended={isExtended}>
											<Typography variant="body2">{data.currentMinPayment}</Typography>
										</StyledTableCell>
									</TableRow>
								</TableBody>
							</Table>
						</SummaryTableContainer>
					</Box>
				)}

				{/* Default Summary Table */}
				<Box>
					<TableTitle variant="body1" color="textSecondary">
						Default caps
					</TableTitle>
					<SummaryTableContainer $isExtended={false}>
						<Table size="small">
							<StyledTableHead $isExtended={false}>
								<TableRow>
									<StyledHeaderCell $isExtended={false} width="33%">
										Balance
									</StyledHeaderCell>
									<StyledHeaderCell $isExtended={false} width="33%">
										Max # of instalments
									</StyledHeaderCell>
									<StyledHeaderCell $isExtended={false} width="34%">
										Min payment
									</StyledHeaderCell>
								</TableRow>
							</StyledTableHead>
							<TableBody>
								<TableRow>
									<StyledTableCell $isExtended={false}>
										<Typography variant="body2">${totalCustomerBalance.toFixed(2)}</Typography>
									</StyledTableCell>
									<StyledTableCell $isExtended={false}>
										<Typography variant="body2">{defaultData.defaultMaxPayments}</Typography>
									</StyledTableCell>
									<StyledTableCell $isExtended={false}>
										<Typography variant="body2">{defaultData.defaultMinPayment}</Typography>
									</StyledTableCell>
								</TableRow>
							</TableBody>
						</Table>
					</SummaryTableContainer>
				</Box>

				{/* Caps History Accordion */}
				{hasHistory && (
					<StyledAccordion expanded={expanded} onChange={handleAccordionChange} elevation={1}>
						<StyledAccordionSummary expandIcon={<ExpandMore />}>
							<Grid container>
								<Grid size={6}>
									<Typography variant="body1">Caps history</Typography>
								</Grid>
							</Grid>
						</StyledAccordionSummary>
						<StyledAccordionDetails>
							<HistoryTableContainer>
								<Table size="small">
									<TableHead>
										<TableRow>
											<StyledHistoryHeaderCell>Type</StyledHistoryHeaderCell>
											<StyledHistoryHeaderCell>Balance</StyledHistoryHeaderCell>
											<StyledHistoryHeaderCell>Max # of payments</StyledHistoryHeaderCell>
											<StyledHistoryHeaderCell>Min payment</StyledHistoryHeaderCell>
											<StyledHistoryHeaderCell>Created at</StyledHistoryHeaderCell>
											<StyledHistoryHeaderCell>Created by</StyledHistoryHeaderCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{history.map((entry, index) => (
											<TableRow key={index}>
												<StyledHistoryTableCell>
													<Typography variant="body2">
														{mapTypeToDisplay(entry.Type)}
													</Typography>
												</StyledHistoryTableCell>
												<StyledHistoryTableCell>
													<Typography variant="body2">
														${(entry.Balance / 100).toFixed(2)}
													</Typography>
												</StyledHistoryTableCell>
												<StyledHistoryTableCell>
													<Typography variant="body2">{entry.MaxPaymentsWeekly}</Typography>
												</StyledHistoryTableCell>
												<StyledHistoryTableCell>
													<Typography variant="body2">
														${(entry.MinPaymentAmount / 100).toFixed(2)}
													</Typography>
												</StyledHistoryTableCell>
												<StyledHistoryTableCell>
													<Typography variant="body2">
														{new Date(entry.PerformedAt).toLocaleDateString("en-GB")}
													</Typography>
												</StyledHistoryTableCell>
												<StyledHistoryTableCell>
													<Typography variant="body2">{entry.AgentEmail}</Typography>
												</StyledHistoryTableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</HistoryTableContainer>
						</StyledAccordionDetails>
					</StyledAccordion>
				)}
			</Stack>
		</StyledContainer>
	)
}

const StyledContainer = styled(Box)`
	background-color: ${colors.white};
	border: 1px solid #a4a7ae;
	border-radius: 0.5rem;
	padding: 1rem;

	box-shadow: 0 4px 14px 2px rgba(54, 59, 100, 0.1);
`

const TableTitle = styled(Typography)`
	margin-bottom: 8px;
`

const getTableBackgroundColor = (isExtended: boolean, isAtMaximum?: boolean) => {
	if (isAtMaximum) return "#FEDF89"
	return isExtended ? "#fffaeb" : "#f6f6f6"
}

const SummaryTableContainer = styled(TableContainer)<{$isExtended: boolean; $isAtMaximum?: boolean}>`
	background-color: ${(props) => getTableBackgroundColor(props.$isExtended, props.$isAtMaximum)};
	border-radius: 4px;
`

const StyledTableHead = styled(TableHead)<{$isExtended: boolean; $isAtMaximum?: boolean}>`
	background-color: ${(props) => getTableBackgroundColor(props.$isExtended, props.$isAtMaximum)};
`

const StyledHeaderCell = styled(TableCell)<{$isExtended?: boolean; $isAtMaximum?: boolean}>`
	font-weight: 500;
	font-size: 0.875rem;
	letter-spacing: 0.17px;
	border-bottom: 1px solid #d5d7da;
	padding: 6px 16px;

	color: ${(props) => (props.$isExtended ? "#7a2e0e" : "#000")};
	font-feature-settings:
		"liga" off,
		"clig" off;
`

const StyledTableCell = styled(TableCell)<{$isExtended: boolean}>`
	border-bottom: none;
	color: ${(props) => (props.$isExtended ? "#7a2e0e" : "inherit")};
`

const StyledAccordion = styled(Accordion)`
	border: 1px solid #d5d7da;
	border-radius: 4px;
	background-color: ${colors.white};

	&:before {
		display: none;
	}

	&.Mui-expanded {
		margin: 0;
	}
`

const StyledAccordionSummary = styled(AccordionSummary)`
	border-bottom: none;
	padding: 0 16px;
	min-height: 48px;

	&.Mui-expanded {
		min-height: 48px;
	}

	.MuiAccordionSummary-content {
		margin: 12px 0;

		&.Mui-expanded {
			margin: 12px 0;
		}
	}
`

const StyledAccordionDetails = styled(AccordionDetails)`
	padding: 0;
`

const HistoryTableContainer = styled(TableContainer)`
	width: 100%;
	padding: 1rem;
	padding-top: 0.5rem;
`

const StyledHistoryHeaderCell = styled(TableCell)`
	color: #000;
	font-size: 0.875rem;
	font-style: normal;
	font-weight: 500;
	line-height: 1.5rem;
	letter-spacing: 0.01063rem;
	border-bottom: 1px solid #e9eaeb;
	padding: 1rem;
`

const StyledHistoryTableCell = styled(TableCell)`
	padding: 1rem;
	border-bottom: 1px solid #e9eaeb;
`

const OverrideActiveChip = styled(Chip)`
	background-color: #f79009;
	color: #ffffff;
	font-size: 13px;
	font-weight: 400;
	line-height: 18px;
	letter-spacing: 0.16px;
	border-radius: 100px;
	height: auto;
	padding: 3px 4px;

	.MuiChip-label {
		padding: 0 6px;
	}
`

const InstalmentCapSection = ({
	customerID,
	totalCustomerBalance,
}: {
	customerID: string
	totalCustomerBalance: number
}) => {
	const [instalmentCapData, setInstalmentCapData] = useState<InstalmentCapsResponse | null>(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		// Don't load instalment caps if customer has zero or negative balance
		if (totalCustomerBalance <= 0) {
			setLoading(false)
			return
		}

		const loadInstalmentCaps = async () => {
			try {
				setLoading(true)
				setError(null)
				const response = await api.customer.getInstalmentCaps(customerID)
				setInstalmentCapData(response)
			} catch (err: any) {
				const errorMessage = err?.Message || "Failed to load instalment caps"
				setError(errorMessage)
				console.error("Error loading instalment caps:", err)
			} finally {
				setLoading(false)
			}
		}

		loadInstalmentCaps()
	}, [customerID, totalCustomerBalance])

	const handleSuccess = async () => {
		const response = await api.customer.getInstalmentCaps(customerID)
		setInstalmentCapData(response)
	}

	const handleError = (errorMessage: string) => {
		setError(errorMessage)
	}

	if (loading) {
		return (
			<StyledInstalmentCapContainer>
				<LoadingPage />
			</StyledInstalmentCapContainer>
		)
	}

	if (error) {
		return (
			<StyledInstalmentCapContainer>
				<Box p={2}>
					<Typography color="error" variant="body2">
						Error loading instalment caps: {error}
					</Typography>
				</Box>
			</StyledInstalmentCapContainer>
		)
	}

	// Check balance before checking if data exists
	if (totalCustomerBalance <= 0) {
		return (
			<StyledInstalmentCapContainer>
				<StyledContainer>
					<Typography variant="body1">
						Instalment Cap is only available for customers with positive balance
					</Typography>
				</StyledContainer>
			</StyledInstalmentCapContainer>
		)
	}

	if (!instalmentCapData) {
		return null
	}

	const currentMinPaymentInDollars = instalmentCapData.ActiveInstalmentCap.MinPaymentAmount / 100
	const defaultMinPaymentInDollars = instalmentCapData.DefaultInstalmentCap.MinPaymentAmount / 100
	const maximumCapAvailable = instalmentCapData.MaximumExtendableDuration

	const componentData: InstalmentCapData = {
		currentMaxPayments: instalmentCapData.ActiveInstalmentCap.MaxPaymentsWeekly,
		currentMinPayment: currentMinPaymentInDollars.toFixed(2),
		maximumCapAvailable: maximumCapAvailable,
	}

	const defaultComponentData: DefaultInstalmentCapData = {
		defaultMaxPayments: instalmentCapData.DefaultInstalmentCap.MaxPaymentsWeekly,
		defaultMinPayment: defaultMinPaymentInDollars.toFixed(2),
	}

	const componentHistory = [...instalmentCapData.InstalmentCaps].sort((a, b) => {
		return new Date(b.PerformedAt).getTime() - new Date(a.PerformedAt).getTime()
	})

	const showResetButton =
		instalmentCapData.ActiveInstalmentCap.MaxPaymentsWeekly !==
		instalmentCapData.DefaultInstalmentCap.MaxPaymentsWeekly

	return (
		<StyledInstalmentCapContainer>
			<InstalmentCap
				data={componentData}
				defaultData={defaultComponentData}
				history={componentHistory}
				customerID={customerID}
				maximumExtendableDuration={instalmentCapData.MaximumExtendableDuration}
				defaultMaxPaymentsWeekly={instalmentCapData.DefaultInstalmentCap.MaxPaymentsWeekly}
				currentMaxPaymentsWeekly={instalmentCapData.ActiveInstalmentCap.MaxPaymentsWeekly}
				totalCustomerBalance={totalCustomerBalance}
				onExtendSuccess={handleSuccess}
				onResetSuccess={handleSuccess}
				onResetError={handleError}
				showResetButton={showResetButton}
			/>
		</StyledInstalmentCapContainer>
	)
}

const StyledInstalmentCapContainer = styled(Box)`
	margin-top: 24px;
	padding: 1rem;
`

export {InstalmentCap, InstalmentCapSection}
